/**
 * CommandPalette (P2.1-S4) — Cmd/Ctrl+K global jumper.
 *
 * Lightweight modal (theo pattern của PublishDialog — không dùng Radix Dialog
 * để giảm dep + fit transitions). Custom keyboard navigation (~80 LOC) thay
 * vì add `cmdk` package.
 *
 * 3 nhóm hiển thị:
 * - HÀNH ĐỘNG NHANH (static)
 * - TRANG (static, filter theo role)
 * - KẾT QUẢ TÌM (dynamic — fetch /api/v1/admin/search?q=, debounce 200ms)
 *
 * Doc 32 §5.
 */
import {
  Bell,
  FileText,
  Languages,
  Loader2,
  Search,
  Workflow,
} from "lucide-react";
import * as React from "react";

import {
  type FormHit,
  type NotificationRuleHit,
  searchApi,
  type SearchResponse,
  type TranslationHit,
  type WorkflowHit,
} from "@/api/search";
import { Input } from "@/components/ui/input";
import {
  filterPaletteItems,
  PAGE_ITEMS,
  type PaletteItem,
  QUICK_ACTIONS,
} from "@/lib/command-registry";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { useCommandPaletteStore } from "@/stores/commandPalette";
import { useRouteStore } from "@/stores/route";

const DEBOUNCE_MS = 200;
const MIN_QUERY_LEN = 2;

type Row =
  | { kind: "item"; item: PaletteItem }
  | { kind: "form"; hit: FormHit }
  | { kind: "workflow"; hit: WorkflowHit }
  | { kind: "rule"; hit: NotificationRuleHit }
  | { kind: "translation"; hit: TranslationHit };

interface Section {
  label: string;
  rows: Row[];
}

export function CommandPalette(): JSX.Element | null {
  const open = useCommandPaletteStore((s) => s.open);
  const query = useCommandPaletteStore((s) => s.query);
  const setQuery = useCommandPaletteStore((s) => s.setQuery);
  const closePalette = useCommandPaletteStore((s) => s.closePalette);

  const navigate = useRouteStore((s) => s.navigate);
  const user = useAuthStore((s) => s.user);
  const roles = user?.roles ?? [];

  const inputRef = React.useRef<HTMLInputElement>(null);
  const [results, setResults] = React.useState<SearchResponse | null>(null);
  const [searching, setSearching] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Auto-focus input + reset state khi mở
  React.useEffect(() => {
    if (open) {
      setActiveIndex(0);
      // Defer focus tới sau khi render
      const t = window.setTimeout(() => inputRef.current?.focus(), 10);
      return () => window.clearTimeout(t);
    }
    setResults(null);
    return undefined;
  }, [open]);

  // Debounced search
  React.useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = window.setTimeout(() => {
      searchApi
        .search(q, 5)
        .then((res) => {
          setResults(res);
        })
        .catch(() => {
          setResults(null);
        })
        .finally(() => setSearching(false));
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, open]);

  // Build sections (filter + dedup)
  const sections: Section[] = React.useMemo(() => {
    const out: Section[] = [];

    const actions = filterPaletteItems(QUICK_ACTIONS, roles, query);
    if (actions.length > 0) {
      out.push({
        label: "Hành động nhanh",
        rows: actions.map((item) => ({ kind: "item", item })),
      });
    }

    const pages = filterPaletteItems(PAGE_ITEMS, roles, query);
    if (pages.length > 0) {
      out.push({
        label: "Trang",
        rows: pages.map((item) => ({ kind: "item", item })),
      });
    }

    if (results) {
      const searchRows: Row[] = [
        ...results.forms.map((hit) => ({ kind: "form" as const, hit })),
        ...results.workflows.map((hit) => ({ kind: "workflow" as const, hit })),
        ...results.notification_rules.map((hit) => ({
          kind: "rule" as const,
          hit,
        })),
        ...results.translations.map((hit) => ({
          kind: "translation" as const,
          hit,
        })),
      ];
      if (searchRows.length > 0) {
        out.push({
          label: `Kết quả tìm (${searchRows.length})`,
          rows: searchRows,
        });
      }
    }

    return out;
  }, [roles, query, results]);

  const flatRows: Row[] = React.useMemo(
    () => sections.flatMap((s) => s.rows),
    [sections],
  );

  // Reset active index khi rows thay đổi
  React.useEffect(() => {
    setActiveIndex(0);
  }, [flatRows.length]);

  const executeRow = React.useCallback(
    (row: Row): void => {
      switch (row.kind) {
        case "item":
          navigate(row.item.route, row.item.param);
          break;
        case "form":
          navigate("admin.forms.edit", row.hit.code);
          break;
        case "workflow":
          navigate("admin.workflows.edit", row.hit.code);
          break;
        case "rule":
          navigate("admin.notifications");
          break;
        case "translation":
          navigate("admin.translations");
          break;
      }
      closePalette();
    },
    [navigate, closePalette],
  );

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      closePalette();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flatRows.length - 1, 0)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const row = flatRows[activeIndex];
      if (row) executeRow(row);
    }
  };

  if (!open) return null;

  let runningIndex = 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4 animate-fade-in"
      onClick={closePalette}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="w-full max-w-xl rounded-xl border bg-popover shadow-xl overflow-hidden flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 border-b px-3 py-2.5">
          <Search className="size-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Tìm form, workflow, hành động..."
            className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-8 px-0 text-sm"
            aria-label="Search query"
          />
          {searching ? (
            <Loader2 className="size-4 text-muted-foreground animate-spin shrink-0" />
          ) : (
            <kbd className="text-[10px] text-muted-foreground border rounded px-1.5 py-0.5 hidden sm:inline">
              Esc
            </kbd>
          )}
        </div>

        {/* Sections */}
        <div className="overflow-y-auto py-2 flex-1">
          {flatRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {query.trim().length < MIN_QUERY_LEN && query.trim().length > 0
                ? `Gõ ít nhất ${MIN_QUERY_LEN} ký tự để tìm…`
                : "Không có kết quả."}
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.label} className="mb-2 last:mb-0">
                <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.label}
                </div>
                {section.rows.map((row) => {
                  const idx = runningIndex++;
                  return (
                    <RowView
                      key={rowKey(row, idx)}
                      row={row}
                      active={idx === activeIndex}
                      onClick={() => executeRow(row)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    />
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t px-3 py-1.5 flex items-center gap-3 text-[10px] text-muted-foreground bg-muted/30">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> chọn
          </span>
          <span className="flex items-center gap-1">
            <Kbd>Enter</Kbd> mở
          </span>
          <span className="flex items-center gap-1">
            <Kbd>Esc</Kbd> đóng
          </span>
        </div>
      </div>
    </div>
  );
}

function rowKey(row: Row, fallbackIdx: number): string {
  switch (row.kind) {
    case "item":
      return `item-${row.item.id}`;
    case "form":
      return `form-${row.hit.code}`;
    case "workflow":
      return `wf-${row.hit.code}`;
    case "rule":
      return `rule-${row.hit.id}`;
    case "translation":
      return `tr-${row.hit.locale}-${row.hit.namespace}-${row.hit.key}`;
    default:
      return `idx-${fallbackIdx}`;
  }
}

interface RowViewProps {
  row: Row;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

function RowView({ row, active, onClick, onMouseEnter }: RowViewProps): JSX.Element {
  const base =
    "flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors";
  const activeCls = active
    ? "bg-primary/10 ring-1 ring-inset ring-primary/30"
    : "hover:bg-muted";

  if (row.kind === "item") {
    const Icon = row.item.icon;
    return (
      <div
        className={cn(base, activeCls)}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
      >
        <Icon className="size-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate">{row.item.label}</div>
          {row.item.hint ? (
            <div className="text-[11px] text-muted-foreground truncate">
              {row.item.hint}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (row.kind === "form") {
    return (
      <div
        className={cn(base, activeCls)}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
      >
        <FileText className="size-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate">
            <code className="text-xs bg-muted px-1 rounded mr-1.5">
              {row.hit.code}
            </code>
            {row.hit.name}
          </div>
          {row.hit.description ? (
            <div className="text-[11px] text-muted-foreground truncate">
              {row.hit.description}
            </div>
          ) : null}
        </div>
        <span className="text-[10px] text-muted-foreground">form</span>
      </div>
    );
  }

  if (row.kind === "workflow") {
    return (
      <div
        className={cn(base, activeCls)}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
      >
        <Workflow className="size-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate">
            <code className="text-xs bg-muted px-1 rounded mr-1.5">
              {row.hit.code}
            </code>
            {row.hit.name}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            v{row.hit.version} · {row.hit.status}
          </div>
        </div>
        <span className="text-[10px] text-muted-foreground">workflow</span>
      </div>
    );
  }

  if (row.kind === "rule") {
    return (
      <div
        className={cn(base, activeCls)}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
      >
        <Bell className="size-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="truncate">{row.hit.name}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            event: {row.hit.event_type}
          </div>
        </div>
        <span className="text-[10px] text-muted-foreground">rule</span>
      </div>
    );
  }

  // translation
  return (
    <div
      className={cn(base, activeCls)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <Languages className="size-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate">
          <code className="text-xs bg-muted px-1 rounded mr-1.5">
            {row.hit.namespace}.{row.hit.key}
          </code>
          <span className="text-[11px] text-muted-foreground">[{row.hit.locale}]</span>
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          → {row.hit.value}
        </div>
      </div>
      <span className="text-[10px] text-muted-foreground">i18n</span>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[16px] px-1 border rounded text-[9px] bg-background">
      {children}
    </kbd>
  );
}
